import React, { useState } from 'react';
import { addDoc, collection } from "firebase/firestore";
import { db } from '../firebase/config';
import { addOns } from '../data/packageData';

const BookingFormModal = ({ selectedPackage, selectedAddOns, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    startTime: '',
    endTime: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const validateDateTime = (date, start, end) => {
    const today = new Date().toISOString().split('T')[0];
    if (date < today) {
      return 'Please select a future date';
    }

    if (start >= end) {
      return 'End time must be after start time';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate date and time
    const timeError = validateDateTime(
      formData.eventDate,
      formData.startTime,
      formData.endTime
    );
    
    if (timeError) {
      setError(timeError);
      return;
    }

    // Validate required fields
    const requiredFields = ['name', 'email', 'phone', 'eventDate', 'startTime', 'endTime'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    // Transform the addOns data to match the expected format in Dart model
    const transformedAddOns = {};
    Object.entries(selectedAddOns).forEach(([id, quantity]) => {
      transformedAddOns[id] = {
        name: addOns[id]?.name || 'Unknown Item',
        price: addOns[id]?.price || 0,
        quantity: quantity
      };
    });

    const bookingData = {
      ...formData,
      package: {
        id: selectedPackage.id,
        name: selectedPackage.name,
        price: selectedPackage.price,
        items: selectedPackage.items
      },
      addOns: transformedAddOns,
      totalAmount: selectedPackage.price + 
        Object.entries(selectedAddOns).reduce((sum, [id, qty]) => 
          sum + (addOns[id]?.price || 0) * qty, 0),
      status: 'pending',
      timestamp: new Date().toISOString()
    };

    try {
      await addDoc(collection(db, 'bookings'), bookingData);
      setIsSuccess(true);
      // Wait 2 seconds before closing to show success message
      setTimeout(() => {
        onClose(true);
      }, 2000);
    } catch (error) {
      console.error("Error submitting booking:", error);
      setError("Failed to submit booking. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {isSuccess ? (
          <div className="p-8 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-playfair font-bold text-gray-900 mb-2">Booking Successful!</h2>
            <p className="text-gray-600">
              Thank you for your booking. We will contact you shortly to confirm the details.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6">
            <h2 className="text-2xl font-playfair font-bold mb-6">Complete Your Booking</h2>
            
            {/* Package Summary */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium mb-2">Package: {selectedPackage.name}</h3>
              <p className="text-sm text-gray-600 mb-2">Base Price: K{selectedPackage.price}</p>
              {Object.keys(selectedAddOns).length > 0 && (
                <div className="text-sm text-gray-600">
                  <p className="mb-1">Add-ons:</p>
                  {Object.entries(selectedAddOns).map(([id, qty]) => (
                    <p key={id}>• {addOns[id]?.name || 'Unknown Item'} × {qty}</p>
                  ))}
                </div>
              )}
            </div>

            {/* Form Fields */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  name="name"
                  required
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  required
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Event Date</label>
                <input
                  type="date"
                  name="eventDate"
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                  value={formData.eventDate}
                  onChange={handleChange}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Time</label>
                  <input
                    type="time"
                    name="startTime"
                    required
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    value={formData.startTime}
                    onChange={handleChange}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">End Time</label>
                  <input
                    type="time"
                    name="endTime"
                    required
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                    value={formData.endTime}
                    onChange={handleChange}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Message (Optional)</label>
                <textarea
                  name="message"
                  rows="3"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.message}
                  onChange={handleChange}
                />
              </div>
            </div>

            {error && (
              <p className="mt-4 text-red-500 text-sm text-center">{error}</p>
            )}

            <div className="mt-6 flex gap-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`flex-1 bg-gold-500 text-white py-2 px-4 rounded-lg hover:bg-gold-600 transition duration-300 ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? 'Processing...' : 'Confirm Booking'}
              </button>
              <button
                type="button"
                onClick={() => onClose(false)}
                disabled={isSubmitting}
                className="flex-1 border border-gray-300 py-2 px-4 rounded-lg hover:bg-gray-50 transition duration-300"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BookingFormModal;
//awesome